<template>
  <page-container>
    <template v-slot:page-fixed-header>
      <v-col cols="12" sm="10" md="10" class="pl-2" v-if="isDataLoading">
        <v-skeleton-loader type="heading" class="text-h3"></v-skeleton-loader>
      </v-col>
      <span
        v-else
        class="text-h5 font-weight-bold ml-3 full-width page-header"
        >{{
          surveyType === "copy"
            ? $t("poll.lblCreateSurvey")
            : routeId
            ? $t("poll.lblEditSurvey")
            : $t("poll.lblCreateSurvey")
        }}</span
      >
      <v-spacer></v-spacer>
      <v-col cols="12" sm="2" md="2" class="pl-2" v-if="isDataLoading">
        <v-skeleton-loader type="heading" class="text-h3"></v-skeleton-loader>
      </v-col>
      <v-tooltip bottom v-if="!isDataLoading">
        <template v-slot:activator="{ on, attrs }">
          <a-btn
            v-if="!isDataLoading"
            class="ml-2"
            color="primaryDark"
            @click.stop="saveSurveyData"
            :disabled="!isDirty || isSurveyExpired || isDescriptionWithImage"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon size="16" class="mr-3">mdi-content-save</v-icon
            >{{ $t("btnSave") }}</a-btn
          >
        </template>
        <span>{{ $t("btnSave") }} </span>
      </v-tooltip>
      <CloseIcon v-if="!isDataLoading" @click="closeSave" classProps="ml-2" />
    </template>
    <template v-slot:page-content>
      <v-col cols="12" sm="12" md="12" class="pa-0" v-if="isDataLoading">
        <v-card flat>
          <v-card-text>
            <v-container>
              <v-skeleton-loader
                type="image,list-item-two-line, list-item-two-line, article, list-item"
              ></v-skeleton-loader>
            </v-container>
          </v-card-text>
        </v-card>
      </v-col>
      <v-form v-else ref="refCreateSurveyForm" v-model="valid" lazy-validation>
        <v-card>
          <v-card-text>
            <v-row>
              <v-col cols="12" sm="12" md="12">
                <a-text-field
                  class="titleField"
                  dense
                  v-model="surveyDetails.title"
                  :requiredMessage="$t('poll.lblSurveyTitleReq')"
                  required
                  :label="$t('poll.lblSurveyTitle')"
                  :disabled="isSurveyExpired"
                ></a-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="12" md="12">
                {{ $t("poll.lblDescription") }}:
                <div
                  id="resize"
                  style="border: solid 1px lightgray; padding-bottom: 1px"
                >
                  <a-rich-text-editor
                    class="survey-description"
                    v-model="surveyDescription"
                    :editorReadonly="isSurveyExpired"
                    @descriptionChange="descriptionChanges"
                  ></a-rich-text-editor>
                </div>
              </v-col>
            </v-row>
            <v-row justify="center">
              <v-col cols="12" sm="12" md="8" lg="8">
                <date-time-range-picker
                  :startDate.sync="surveyDetails.startDate"
                  :endDate.sync="surveyDetails.endDate"
                  :endDateAutoIncrease="1440"
                ></date-time-range-picker>
              </v-col>
              <v-col cols="12" sm="12" md="4" lg="4">
                <v-switch
                  dense
                  hide-details
                  :label="lblAnonymous"
                  v-model="surveyDetails.isAnonymous"
                  :disabled="isSurveyExpired"
                ></v-switch>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <meeting-participants
                  v-if="participantsShowHide"
                  :meetingAtteendeelist="surveyDetails.surveyParticipant"
                  :suggestedList="suggestedList"
                  @onSelection="onParticipantChanged"
                  mode="create"
                  type="target"
                  :isAttendees="false"
                  :adHocEmail="false"
                  :label="`${$t('poll.lblSelectTarget')}`"
                  :isAlternateEmailShow="false"
                  :disabled="isSurveyExpired"
                ></meeting-participants>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <transition name="slide-fade">
          <v-card v-if="false" class="mx-auto mt-3" max-width="80%" outlined>
            <v-container>
              <add-question v-model="newQuestion"></add-question>
            </v-container>
          </v-card>
        </transition>
        <transition name="slide-fade">
          <questions-panel
            v-if="sectionNotFound"
            v-model="questionsList"
            :panel.sync="panel"
          />
          <section-panel
            v-else
            v-model="surveyDetails"
            :panel.sync="sectionPanel"
            :surveyeButtonDisable="surveyeButtonDisable"
            @interface="getSectionChildInterface"
          ></section-panel>
        </transition>
        <add-question-card
          v-if="sectionNotFound && !isSurveyExpired"
          @addQuestions="addQuestions"
        ></add-question-card>
      </v-form>
      <message-sheet
        :showMessage="displayMessage"
        :message="message"
        :messageType="messageType"
        @close="displayMessage = false"
      ></message-sheet>
      <prevent-navigation
        :prevent="isDescriptionWithImage ? false : isDirty && !isSaved"
        @save="saveOnLeave"
        @discard="$event.next()"
        @cancel="$event.next(false)"
      >
      </prevent-navigation>
    </template>
  </page-container>
</template>

<script>
import DateTimeRangePicker from "../DateTimeRangePicker.vue";
import AddQuestion from "./AddQuestion.vue";
import _ from "lodash";
import { createUUID } from "../../utils/util";
import { colorPalettes } from "../../utils/constant";
import draggable from "vuedraggable";
import QuestionDrawer from "./QuestionDrawer.vue";
import typeComponents from "../poll/components/index";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import MessageSheet from "../MessageSheet.vue";
import Vue from "vue";
import {
  RichTextEditorPlugin,
  Toolbar,
  Link,
  Count,
  HtmlEditor,
  QuickToolbar,
  Table,
  Resize,
} from "@syncfusion/ej2-vue-richtexteditor";
import SurveySection from "./components/SurveySection.vue";
import QuestionsPanel from "./QuestionsPanel.vue";
import SectionPanel from "./SectionPanel.vue";
import AddQuestionCard from "./AddQuestionCard.vue";
import Loader from "../Loader.vue";
import SignalRMixin from "../../mixin/SignalRMixin";
import PreventNavigation from "@/components/PreventNavigation.vue";
import MeetingParticipants from "../agenda/MeetingParticipants.vue";

Vue.use(RichTextEditorPlugin);
export default {
  provide: {
    richtexteditor: [
      Toolbar,
      Link,
      Count,
      HtmlEditor,
      QuickToolbar,
      Resize,
      Table,
    ],
  },
  components: {
    AddQuestion,
    DateTimeRangePicker,
    draggable,
    QuestionDrawer,
    MessageSheet,
    SurveySection,
    QuestionsPanel,
    SectionPanel,
    AddQuestionCard,
    Loader,
    PreventNavigation,
    MeetingParticipants,
  },
  name: "CreateSurvey",
  mixins: [SignalRMixin],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isDescriptionWithImage: false,
      participantsShowHide: false,
      originalSurveyData: {},
      isDataLoading: false,
      isFormValidate: false,
      valid: true,
      displayMessage: false,
      // loadingMessage: this.$t("LoaderText"),
      messageType: "",
      message: "",
      routeId: null,
      selectedQuestionType: null,
      panel: 0,
      sectionPanel: 0,
      showQuestionCard: false,
      showDrawer: false,
      windowTransition: "scale-transition",
      showQuestionTypeOptions: false,
      richtexteditorEnable: true,
      newQuestion: {
        number: null,
        sectionId: null,
        question: "",
        options: [
          { text: "", value: "", fill: "" },
          { text: "", value: "", fill: "" },
        ],
        isMandatory: false,
        isMultipleAnswer: false,
        isAskComment: false,
        type: "choice",
        id: null,
        isEdit: true,
        selectedAnswer: null,
        subtitle: "",
        isSubtitle: false,
        isLabel: true,
        minLabel: "Min",
        maxLabel: "High",
      },
      newQuestionRef: {
        number: null,
        sectionId: null,
        question: "",
        options: [
          { text: "", value: "", fill: "" },
          { text: "", value: "", fill: "" },
        ],
        isMandatory: false,
        isMultipleAnswer: false,
        isAskComment: false,
        type: "choice",
        id: null,
        isEdit: true,
        selectedAnswer: null,
        subtitle: "",
        isSubtitle: false,
        isLabel: true,
        minLabel: "Min",
        maxLabel: "High",
      },
      newSectionRef: {
        sectionId: null,
        title: "",
        description: "",
      },
      newSection: {
        sectionId: null,
        title: "",
        description: "",
      },
      showQuestionDialog: false,
      options: [""],
      optionLabel: "Option",
      startDate: new Date().toISOString(),
      endDate: new Date().toISOString(),
      isSaved: false,
      surveyTargetUsersList: [],
    };
  },
  computed: {
    ...mapState({
      surveyDetails: (state) => state.poll.surveyDetails,
      refSurveyDetails: (state) => state.poll.refSurveyDetails,
      surveyResponseResult: (state) => state.poll.surveyResponseResult,
    }),
    ...mapGetters({
      isSurveyExpired: "poll/isSurveyExpired",
    }),
    isDirty() {
      let originalSurveyObject = {
        ...this.originalSurveyData,
        description: this.originalSurveyData.description || "",
      };
      return (
        JSON.stringify(originalSurveyObject) !==
        JSON.stringify(this.surveyDetails)
      );
    },
    surveyStatus() {
      let startDate = new Date(this.surveyDetails.startDate);
      let endDate = new Date(this.surveyDetails.endDate);
      let today = new Date();
      if (endDate < today) {
        return "expired";
      } else if (startDate > today) {
        return "upcoming";
      } else if (startDate <= today) {
        return "active";
      } else {
        return "";
      }
    },
    surveyeditable() {
      let startDate = new Date(this.surveyDetails.startDate);
      let endDate = new Date(this.surveyDetails.endDate);
      let today = new Date();

      if (
        this.surveyDetails.id != null &&
        startDate >= today &&
        endDate <= today
      ) {
        //active
        return false;
      } else if (
        this.surveyDetails.id != null &&
        startDate <= today &&
        endDate >= today
      ) {
        return false;
      } else {
        return true;
      }
    },
    surveyeButtonDisable() {
      let startDate = new Date(this.surveyDetails.startDate);
      let endDate = new Date(this.surveyDetails.endDate);
      let today = new Date();

      if (this.surveyDetails.id != null && endDate > today) {
        return true;
      } else if (this.surveyDetails.id != null && endDate <= today) {
        return false;
      } else {
        return true;
      }
    },
    questionsList() {
      return this.surveyDetails.questions;
    },
    answerOptions() {
      return [
        {
          text: this.$t("poll.lblChoice"),
          value: "choice",
          icon: "mdi-radiobox-marked",
        },
        {
          text: this.$t("poll.lblText"),
          value: "textType",
          icon: "mdi-text-short",
        },
        { text: this.$t("poll.lblRating"), value: "rating", icon: "mdi-star" },
        { text: this.$t("poll.lblLikert"), value: "likert", icon: "mdi-grid" },
        {
          text: this.$t("poll.lblSection"),
          value: "section",
          icon: "mdi-selection-multiple",
        },
      ];
    },
    lblAnonymous() {
      return this.$t("poll.lblAnonymous");
    },
    questionList() {
      return this.surveyDetails.question;
    },
    lblOption() {
      return this.$t("poll.lblOption");
    },
    lblStatement() {
      return this.$t("poll.lblStatement");
    },

    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value || false);
      },
    },

    openseq: {
      get() {
        return null;
      },
      set(value) {
        this.$emit("input", value || false);
      },
    },

    surveyDescription: {
      get() {
        return this.surveyDetails.description
          ? this.surveyDetails.description
          : "";
      },
      set(value) {
        this.surveyDetails.description = value;
      },
    },
    currentTitle() {
      switch (this.step) {
        case 1:
          return "Sign-up";
        case 2:
          return "Create a password";
        default:
          return "Account created";
      }
    },
    sections() {
      return this.surveyDetails.sections;
    },
    questionList() {
      return this.surveyDetails.questions;
    },
    sectionNotFound() {
      if (!this.surveyDetails.sections) {
        return true;
      }
      if (this.surveyDetails.sections.length > 0) {
        return false;
      }

      return true;
    },
    selectedTenant() {
      return sessionStorage.getItem("tenantId");
    },
    LoaderText() {
      return this.$t("LoaderText");
    },
    surveyType() {
      return this.$route.params.type || "";
    },
    suggestedList() {
      return this.surveyTargetUsersList && this.surveyTargetUsersList.length > 0
        ? this.surveyTargetUsersList.map((v) => ({
            ...v,
            email: v.registered_email,
          }))
        : [];
    },
    lblCopy() {
      return this.$t("poll.tipCopy");
    },
  },
  async mounted() {
    const today = new Date();
    const startDate = new Date().toISOString();
    const endDate = new Date(today.getTime() + 1440 * 60000).toISOString();
    try {
      this.$refs.refCreateSurveyForm.resetValidation();
      this.$refs.refCreateSurveyForm.reset();
      this.participantsShowHide = false;
      setTimeout(() => {
        this.participantsShowHide = true;
      }, 10);
      this.resetSurveyDetails();
      this.setSurveyDetails({
        ...this.surveyDetails,
        startDate: startDate,
        endDate: endDate,
      });
      let id = this.$route.params.id;
      if (this.$route.params.surveyid && this.$route.params.surveyid != "") {
        id = this.$route.params.surveyid;
      }
      this.originalSurveyData = _.cloneDeep(this.surveyDetails);
      if (id && id !== "new") {
        this.routeId = id;
        this.isDataLoading = true;
        if (this.$security.checkUserAccess("ViewSurveyPolls")) {
          const result = await this.getSurveyById(id);
        }
        this.originalSurveyData = _.cloneDeep(this.surveyDetails);
        this.isDataLoading = false;
        this.showQuestionCard = true;
        this.panel = null;
        this.sectionPanel = 0;
        this.signalRInit(`surveys_${this.selectedTenant}`);
        this.signalRUserResponse(`surveys_${id}`);
        if (this.surveyType === "copy") {
          this.setSurveyDetails({
            ...this.surveyDetails,
            id: null,
            title: `${this.lblCopy} ${this.surveyDetails.title}`,
            startDate: startDate,
            endDate: endDate,
          });
        }
      }
      this.$root.$on("add-question-type", (id) => {
        this.addQuestions();
      });
      this.$root.$on("add-question-option", (question, sectionId) => {
        this.onOptionClick(question, sectionId);
      });
      const thisInstance = this;
      setTimeout(() => {
        if (thisInstance.$refs.rteObj) {
          thisInstance.$refs.rteObj.refreshUI();
        }
      }, 100);
    } catch (error) {
      this.isDataLoading = false;
    }
  },
  beforeDestroy() {
    this.$eventHub.$off("sectionDescriptionChange");
    this.$root.$off("add-question-type");
    this.$root.$off("add-question-option");
  },
  async created() {
    this.setSurveyResponseResult([]);
    const id = this.$route.params.id;
    if (
      id &&
      id !== "new" &&
      this.$security.checkUserAccess("ViewSurveyResponse")
    ) {
      await this.getResponseBySurveyId(id);
    }
    this.surveyTargetUsersList = await this.getSurveyTargetUsersList();
    this.$eventHub.$on("sectionDescriptionChange", (val) => {
      this.isDescriptionWithImage = val;
    });
  },
  methods: {
    ...mapActions({
      createSurvey: "poll/createSurvey",
      getSurveyById: "poll/getSurveyById",
      updateSurvey: "poll/updateSurvey",
      getSurveyList: "poll/getSurveyList",
      getResponseBySurveyId: "poll/getResponseBySurveyId",
      getSurveyTargetUsersList: "poll/getSurveyTargetUsersList",
    }),
    ...mapMutations({
      resetSurveyDetails: "poll/RESET_SURVEY_OBJECT",
      addSurveyQuestion: "poll/ADD_SURVEY_QUESTION",
      setSurveyDetails: "poll/GET_SURVEY_BY_ID",
      setSurveyResponseResult: "poll/GET_SURVEY_RESPONSE_RESULT",
    }),
    createUUID,
    close,
    closeSave,
    addQuestions,
    addNewQuestion,
    onEditQuestion,
    onDeleteQuestion,
    onOptionClick,
    saveSurvey,
    saveSurveyData,
    loadTypeComponent,
    updateSurveyDetails,
    addNewSurveyDetails,
    signalRInit,
    saveOnLeave,
    descriptionChanges(val) {
      this.isDescriptionWithImage = val;
    },
    showMessageSheet(message, messageType) {
      this.displayMessage = true;
      this.message = message;
      this.messageType = messageType;
    },
    onBlur: function (e) {
      this.$refs.rteObj.ej2Instances.toolbarSettings.enable = false;
    },
    onFocus: function (e) {
      this.$refs.rteObj.ej2Instances.toolbarSettings.enable = true;
    },
    getSectionChildInterface(childInterface) {
      this.$options.sectionChildInterface = childInterface;
    },
    navigateToOrigin,
    optionHighlight,
    questionHighlight,
    signalRUserResponse,
    onParticipantChanged,
    CheckCreateSurveyForm,
    handleFocusBlur,
  },
};

function onParticipantChanged(participant) {
  this.surveyDetails.surveyParticipant = participant.map((item) => ({
    id: item.id,
    email: item.email,
  }));
}

async function saveOnLeave({ next }) {
  if (this.$refs.refCreateSurveyForm.validate()) {
    if (this.surveyDetails.questions.length === 0) {
      this.isFormValidate = false;
      this.showMessageSheet(this.$t("poll.lblQuestionValidationMsg"), "error");
      return false;
    } else if (this.isSurveyExpired) {
      this.showMessageSheet(
        this.$t("poll.msgSurveyExpired", { type: "Survey" }),
        "error"
      );
      return false;
    }
    await this.saveSurvey().then((res) => {
      next();
    });
  }
}

function signalRInit(target) {
  this.signalConnection.on(target, async (message) => {
    const result = JSON.parse(message);
    if (result) {
      const id = this.$route.params.id;
      if (id) {
        if (this.$security.checkUserAccess("ViewSurveyPolls")) {
          await this.getSurveyById(id);
        }
        this.originalSurveyData = _.cloneDeep(this.surveyDetails);
      }
      if (!this.surveyDetails) {
        await this.$refs.confirm.open(
          this.$t("poll.lblSurveyDeleteWarning"),
          this.$t("poll.msgSurveyDeleteWarning"),
          { dialogIcon: "alert", noconfirm: true }
        );
        this.$router.push({ path: "/" });
      }
    }
  });
  if (this.signalConnection.connectionState.toLowerCase() === "disconnected") {
    this.signalConnection.start().catch();
  }
}

function signalRUserResponse(target) {
  this.signalConnection.on(target, async (message) => {
    const result = JSON.parse(message);
    if (result) {
      const id = this.$route.params.id;
      if (id && this.$security.checkUserAccess("ViewSurveyResponse")) {
        await this.getResponseBySurveyId(id);
      }
    }
  });
  if (this.signalConnection.connectionState.toLowerCase() === "disconnected") {
    this.signalConnection.start().catch();
  }
}

function loadTypeComponent(type) {
  return typeComponents[type] || null;
}

async function saveSurveyData() {
  if (this.$refs.refCreateSurveyForm.validate()) {
    this.isSaved = true;
    await this.saveSurvey();
  } else {
    this.CheckCreateSurveyForm();
  }
}

function CheckCreateSurveyForm() {
  if (
    this.$refs.refCreateSurveyForm.inputs &&
    this.$refs.refCreateSurveyForm.inputs.length > 0
  ) {
    try {
      this.$refs.refCreateSurveyForm.inputs.forEach((item, i) => {
        if (
          !item.valid &&
          item.$refs.input.getAttribute("required") == "required"
        ) {
          const expansionElem = item.$refs.input.closest(".question-panel");
          if (expansionElem) {
            const sectionPanel = expansionElem.closest(".section-panels");
            if (sectionPanel) {
              this.$options.sectionChildInterface.openSection(
                sectionPanel.dataset.section
              );
            } else if (expansionElem) {
              if (this.$options.childInterface) {
                this.$options.childInterface.openPanel(
                  expansionElem.dataset.question
                );
              }
            }
          }
          setTimeout(() => {
            if (item.$refs.input) {
              item.$refs.input.focus();
            }
          }, 200);
          throw new Error("Break the loop.");
        }
      });
    } catch (error) {
      console.log("Exception thrown from the forEach loop. ", error);
    }
  }
}

async function saveSurvey() {
  try {
    this.richtexteditorEnable = false;
    this.isFormValidate = true;
    if (this.surveyDetails.questions.length === 0) {
      this.isFormValidate = false;
      this.showMessageSheet(this.$t("poll.lblQuestionValidationMsg"), "error");
    }
    const sections = this.surveyDetails.sections;
    let isSectonExistQuestion = true;
    let secId = null;
    sections.forEach((section) => {
      const isExist = this.surveyDetails.questions.some(
        (q) => q.sectionId === section.sectionId
      );
      if (!isExist) {
        isSectonExistQuestion = isExist;
        secId = section.sectionId;
      }
    });
    if (!isSectonExistQuestion) {
      this.isFormValidate = false;
      const sectionIndex = this.surveyDetails.sections.findIndex(
        (q) => q.sectionId === secId
      );
      this.showMessageSheet(this.$t("poll.lblQuestionValidationMsg"), "error");
      if (sectionIndex) {
        this.$options.sectionChildInterface.openSection(sectionIndex);
      }
    }
    if (this.$refs.refCreateSurveyForm.validate() && this.isFormValidate) {
      this.loadingMessage = this.$t("SaveLoader");
      const surveyId = this.surveyDetails.id;
      if (this.surveyDetails.id != null) {
        await this.updateSurveyDetails();
      } else {
        await this.addNewSurveyDetails();
      }
      this.loadingMessage = this.LoaderText;
      this.isFormValidate = false;
      this.$store.commit("setAlert", {
        variant: "success",
        message:
          surveyId != null
            ? this.$t("poll.SurveyUpdateSuccess")
            : this.$t("poll.SurveyCreateSuccess"),
      });
      if (this.isSaved == true) {
        this.close();
      }
    } else {
      this.CheckCreateSurveyForm();
    }
    this.richtexteditorEnable = true;
  } catch (error) {
    console.log(error);
  }
}

async function addNewSurveyDetails() {
  this.surveyDetails.id = createUUID();
  this.surveyDetails.userid = this.$auth.user.tenantUserName;
  if (this.surveyDetails.tenantid !== this.selectedTenant) {
    this.surveyDetails.tenantid = this.selectedTenant;
  }
  this.surveyDetails.createdDate = new Date().toISOString();
  await this.createSurvey(_.cloneDeep(this.surveyDetails));
}

async function updateSurveyDetails() {
  await this.updateSurvey(_.cloneDeep(this.surveyDetails));
}

function onDeleteQuestion(question) {
  this.surveyDetails.questions = this.surveyDetails.questions.filter(
    (value) => value.id !== question.id
  );
}

function onEditQuestion(question) {
  this.newQuestion = Object.assign({}, question);
}

function onOptionClick(question, sectionId = null) {
  let surveyDetailsQuestions = _.cloneDeep(this.surveyDetails.questions);
  if (sectionId) {
    surveyDetailsQuestions = surveyDetailsQuestions.filter(function (el) {
      return el.sectionId == sectionId;
    });
  }
  const inValidQuestions = [];
  if (surveyDetailsQuestions.length > 0) {
    surveyDetailsQuestions.forEach((item, i) => {
      if (item.type === "rating") {
        if (item.options.text === "" || item.options.value === 0) {
          inValidQuestions.push(item); // main item
        }
      }
      if (item.question == "") {
        inValidQuestions.push(item);
      }
      if (item.type == "choice") {
        item.options.forEach((option) => {
          if (option.text == "") {
            inValidQuestions.push(item); // main item
          }
        });
      }
    });
    if (inValidQuestions.length > 0) {
      const question = inValidQuestions[0];
      if (question.question == "") {
        this.questionHighlight(question);
      } else if (
        (question.type === "rating" && question.options.text === "") ||
        question.options.value === 0
      ) {
        this.questionHighlight(question);
      } else if (question.type == "choice") {
        const optionItems = [];
        question.options.forEach((option, i) => {
          if (option.text == "") {
            option.index = i;
            optionItems.push(option);
          }
        });
        if (optionItems.length > 0) {
          this.optionHighlight(optionItems[0], question.id);
        }
      }
      this.showQuestionTypeOptions = false;
      return false;
    }
    if (!this.$refs.refCreateSurveyForm.validate()) {
      return false;
    }
  }
  if (sectionId && question.value == "section") {
    const isExist = this.surveyDetails.questions.some(
      (q) => q.sectionId === sectionId
    );
    if (!isExist) {
      this.isFormValidate = false;
      this.showMessageSheet(this.$t("poll.lblQuestionValidationMsg"), "error");
      this.showQuestionTypeOptions = false;
      return false;
    }
  }

  if (question.value !== "section") {
    this.selectedQuestionType = question.value;
    this.newQuestion = _.cloneDeep(this.newQuestionRef);
    this.newQuestion.type = question.value;
    if (question.value == "textType") {
      this.newQuestion.options = "";
    }
    if (question.value == "rating") {
      this.newQuestion.options = { text: "", value: 0 };
    }
    if (question.value == "likert") {
      let options = [];
      for (let index = 1; index <= 5; index++) {
        options.push({
          text: `${this.lblOption} ${index}`,
          value: createUUID(),
          isActive: false,
        });
      }
      this.newQuestion.options = {
        options,
        statement: [
          { text: this.lblStatement + " 1", value: null },
          { text: this.lblStatement + " 2", value: null },
        ],
      };
    }

    this.newQuestion.number = this.surveyDetails.questions.length + 1;
    this.newQuestion.id = createUUID();
    this.newQuestion.sectionId = sectionId;
    this.addSurveyQuestion(this.newQuestion);
    this.panel = this.surveyDetails.questions.length - 1;
    this.showQuestionTypeOptions = false;
    this.showQuestionCard = true;
  } else {
    this.newSection = _.cloneDeep(this.newSectionRef);
    this.newSection.sectionId = createUUID();
    if (this.surveyDetails.sections.length === 0) {
      this.surveyDetails.sections.push(this.newSection);
      this.surveyDetails.questions = this.surveyDetails.questions.map(
        (item) => ({
          ...item,
          sectionId: this.newSection.sectionId,
        })
      );

      this.showQuestionCard = true;

      if (this.surveyDetails.questions.length > 0 && false) {
        /* Removing Section (ref. #1121) #1225 (After confirm remove this code)*/
        let section = {
          sectionId: createUUID(),
          title: "",
          description: "",
        };
        this.surveyDetails.sections.push(section);
      }
    } else {
      const index = this.surveyDetails.sections.findIndex(
        (item) => item.sectionId == sectionId
      );
      let section = {
        sectionId: createUUID(),
        title: "",
        description: "",
      };
      this.surveyDetails.sections.splice(index + 1, 0, section);
    }
    this.sectionPanel = this.surveyDetails.sections.length - 1;
    this.showQuestionTypeOptions = false;
    this.showQuestionCard = true;
  }
}

function optionHighlight(option, questionId) {
  const elEm = document.getElementById(
    `question-option-${option.index}-${questionId}`
  );
  if (elEm.getAttribute("required") == "required") {
    elEm.focus();
    setTimeout(() => {
      elEm.blur();
    }, 100);
  }
}

function questionHighlight(question) {
  const elements = {
    rating: document.getElementById(`question-rating-${question.id}`),
    label: document.getElementById(`question-rating-lavel-${question.id}`),
  };

  // Focus and blur rating element if conditions are met
  if (
    elements.rating &&
    question.options.text === "" &&
    elements.rating.hasAttribute("required")
  ) {
    this.handleFocusBlur(elements.rating);
  }

  // Focus and blur label element if conditions are met
  if (
    elements.label &&
    question.options.value === 0 &&
    elements.label.hasAttribute("required")
  ) {
    this.handleFocusBlur(elements.label);
  }

  const elEm = document.getElementById(`question-${question.id}`);

  if (elEm) {
    const labelElms = elEm.querySelector("input, textarea");
    if (labelElms && labelElms.required) {
      this.handleFocusBlur(labelElms);
    }
  }
}

function handleFocusBlur(element) {
  element.focus();
  setTimeout(() => {
    element.blur();
  }, 100);
}

function addQuestions() {
  this.showQuestionTypeOptions = !this.showQuestionTypeOptions;
}

function addNewQuestion() {
  if (this.newQuestion.id) {
    let result = this.surveyDetails.questions.find(
      (question) => question.id === this.newQuestion.id
    );
    if (result) {
      result.question = this.newQuestion.question;
      result.options = this.newQuestion.options;
      result.isMandatory = this.newQuestion.isMandatory;
    }
  } else {
    this.newQuestion.id = createUUID();
    this.surveyDetails.questions.push(this.newQuestion);
  }
}

function closeSave() {
  this.$emit("input", false);
  this.navigateToOrigin();
}
function close() {
  this.$refs.refCreateSurveyForm.resetValidation();
  this.$refs.refCreateSurveyForm.reset();
  this.closeSave();
}

function navigateToOrigin() {
  switch (this.$route.params.origin) {
    case "dashboard":
      this.$router.push("/dashboard");
      break;
    case "survey":
      this.$router.push("/survey");
      break;
    case "calendar":
      this.$router.push("/calendar");
      break;
    default:
      this.$router.push("/");
      break;
  }
}
</script>

<style scoped>
.survey-description /deep/ .e-rte-link-dialog.e-dialog {
  z-index: 1 !important;
}
.survey-description /deep/ .e-richtexteditor {
  z-index: 0 !important;
}
.titleField {
  font-family: "Noto Sans JP" !important;
  font-style: normal !important;
  font-weight: 400 !important;
  font-size: 14px !important;
  line-height: 18px !important;
  min-height: 48px !important;
  max-height: 68px !important;
  color: var(--v-neutralGray-base) !important;
  align-items: center !important;
}
.titleField > .v-input__control > .v-input__slot {
  color: var(--v-neutralGray-base) !important;
  height: 48px !important;
}
.titleField /deep/ .v-input__slot {
  min-height: 48px !important;
}
.questions-drag-icon {
  color: rgba(white, 0.35);
  cursor: move;
  margin-right: 0.8rem;
}
/* Enter and leave animations can use different */
/* durations and timing functions.              */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}
.slide-fade-leave-active {
  transition: all 0.8s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter, .slide-fade-leave-to
/* .slide-fade-leave-active below version 2.1.8 */ {
  transform: translateX(10px);
  opacity: 0;
}

.fabric-dark .cm-s-default .cm-tag,
.bootstrap5-dark .cm-s-default .cm-tag,
.material-dark .cm-s-default .cm-tag,
.tailwind-dark .cm-s-default .cm-tag,
.highcontrast .cm-s-default .cm-tag {
  color: #00ff00;
}

.fabric-dark .cm-s-default .cm-string,
.bootstrap5-dark .cm-s-default .cm-string,
.material-dark .cm-s-default .cm-string,
.tailwind-dark .cm-s-default .cm-string {
  color: blue;
}

.highcontrast .cm-s-default .cm-string {
  color: #ffd939;
}

.fabric-dark .cm-s-default .cm-attribute,
.bootstrap5-dark .cm-s-default .cm-attribute,
.material-dark .cm-s-default .cm-attribute,
.tailwind-dark .cm-s-default .cm-attribute,
.highcontrast .cm-s-default .cm-attribute {
  color: #f00;
}

.fabric-dark .CodeMirror,
.bootstrap5-dark .CodeMirror,
.material-dark .CodeMirror,
.tailwind-dark .CodeMirror {
  background: #303030;
  color: white;
}

.highcontrast .CodeMirror {
  background: black;
  color: white;
}

.e-richtexteditor {
  color: #333;
  display: block;
  position: relative;
  z-index: 0;
}
.e-richtexteditor .e-rte-content .e-content pre {
  padding: 10px;
  background: #f4f5f7;
}

.fabric-dark .e-richtexteditor .e-rte-content .e-content pre,
.bootstrap5-dark .e-richtexteditor .e-rte-content .e-content pre,
.material-dark .e-richtexteditor .e-rte-content .e-content pre,
.tailwind-dark .e-richtexteditor .e-rte-content .e-content pre,
.highcontrast .e-richtexteditor .e-rte-content .e-content pre {
  padding: 10px;
  background: #303030;
}
#resize .e-richtexteditor {
  max-width: 100%;
  min-width: 100%;
  min-height: 150px;
  max-height: 300px;
}
::v-deep .e-datetime-wrapper {
  height: 40px !important;
}
</style>
